import { FormEvent, useEffect, useState } from "react";
import QRCode from "qrcode";
import { ShieldCheck } from "lucide-react";
import { useAuth } from "../auth/OAuthContext";
import { AuthScreen } from "../components/AuthScreen";
import { OtpCodeInput } from "../components/OtpCodeInput";

function normalizeMfaCode(value: string): string {
  return value.replace(/\D/g, "").slice(0, 6);
}

export function MfaEnrollment() {
  const { mfaEnrollment, startMfaEnrollment, verifyMfaEnrollment, logout, loading } = useAuth();
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!mfaEnrollment) {
      setError(null);
      void startMfaEnrollment().catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Could not start MFA setup");
        }
      });
    }
    return () => {
      cancelled = true;
    };
  }, [mfaEnrollment, startMfaEnrollment]);

  useEffect(() => {
    if (!mfaEnrollment) {
      setQrDataUrl(null);
      return;
    }
    QRCode.toDataURL(mfaEnrollment.provisioningUri, { margin: 1, width: 200 })
      .then(setQrDataUrl)
      .catch(() => {
        setQrDataUrl(null);
      });
  }, [mfaEnrollment]);

  async function handleVerify(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const normalizedCode = normalizeMfaCode(code);
    if (normalizedCode.length !== 6) {
      setError("Enter the 6-digit code from your authenticator app");
      return;
    }
    try {
      await verifyMfaEnrollment(normalizedCode);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid code");
    }
  }

  return (
    <AuthScreen>
      <div className="w-full flex flex-col gap-5">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs font-semibold self-start">
          <ShieldCheck size={16} aria-hidden="true" />
          <span>Secure setup</span>
        </span>

        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight font-display">
            Set up two-factor authentication
          </h1>
          <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">
            Scan the QR code with your authenticator app, then enter the 6-digit code.
          </p>
        </div>

        {!mfaEnrollment && (
          <p className="text-sm text-slate-500 italic py-4">Generating your setup code…</p>
        )}

        {mfaEnrollment && (
          <>
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
              {qrDataUrl && (
                <img
                  className="w-36 h-36 rounded-xl bg-white p-2 border border-slate-200 shadow-xs shrink-0"
                  src={qrDataUrl}
                  alt="Scan with your authenticator app"
                />
              )}
              <div className="flex flex-col gap-1.5 text-xs text-slate-600">
                <strong className="text-sm font-bold text-slate-900">Scan with your app</strong>
                <p>
                  Use your phone camera or{" "}
                  <a
                    href={mfaEnrollment.provisioningUri}
                    className="text-[#012699] font-bold underline hover:text-[#0b2981]"
                  >
                    open directly
                  </a>
                  .
                </p>
                <span className="text-[11px] font-semibold text-slate-400 mt-1 uppercase tracking-wider">
                  Manual key
                </span>
                <code className="p-1.5 bg-white border border-slate-200 rounded-lg font-mono text-xs font-semibold text-slate-800 break-all select-all">
                  {mfaEnrollment.secret}
                </code>
              </div>
            </div>

            <form onSubmit={handleVerify} className="flex flex-col gap-4">
              <OtpCodeInput
                id="enrollment-mfa-code"
                label="Verification code"
                value={code}
                onChange={(nextCode) => {
                  setCode(nextCode);
                  setError(null);
                }}
                invalid={!!error}
                autoFocus
              />
              {error && (
                <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm font-medium">
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={loading || code.length !== 6}
                className="w-full min-h-[46px] flex items-center justify-center font-bold text-sm text-white bg-[#012699] hover:bg-[#0b2981] active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-all shadow-sm cursor-pointer"
              >
                {loading ? "Verifying..." : "Verify and continue"}
              </button>
            </form>
          </>
        )}

        {!mfaEnrollment && error && (
          <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm font-medium">
            {error}
          </div>
        )}

        <button
          type="button"
          onClick={() => void logout()}
          className="w-full py-2.5 text-xs font-bold text-slate-500 hover:text-slate-800 transition-colors cursor-pointer"
        >
          Cancel and sign out
        </button>
      </div>
    </AuthScreen>
  );
}
